// src/components/DepensesManagement.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import MaterielMobilierManagement from './MaterielMobilierManagement'; // Importation du composant de gestion de matériel
import { Plus, Edit, Trash2, Zap, DollarSign, Briefcase } from 'lucide-react'; // Ajout d'icônes

const DepensesManagement = ({ db, schoolId, userRole, setMessage, setError }) => {
    // Rôles et Permissions (inchangé)
    const canManageExpenses = ['superAdmin', 'admin', 'financier'].includes(userRole);
    const [activeTab, setActiveTab] = useState('generalExpenses'); 

    // --- États et logique pour l'onglet Dépenses Générales (Charges d'exploitation) ---
    const [generalExpenses, setGeneralExpenses] = useState([]);
    const [newGeneralExpense, setNewGeneralExpense] = useState({ description: '', amount: '', date: '', category: '' });
    const [editingGeneralExpenseId, setEditingGeneralExpenseId] = useState(null);

    // --- États et logique pour l'onglet Gestion de Paie (Charges de Personnel) ---
    const [payrollData, setPayrollData] = useState([]);
    const [employeesForPayroll, setEmployeesForPayroll] = useState([]); 
    const [newPayrollEntry, setNewPayrollEntry] = useState({ employeeId: '', employeeName: '', amount: '', date: '', period: '' });
    const [editingPayrollId, setEditingPayrollId] = useState(null);

    // --- NOUVEAUX ÉTATS pour Dettes & Emprunts (Passif du Bilan) ---
    const [supplierDebts, setSupplierDebts] = useState([]);
    const [newSupplierDebt, setNewSupplierDebt] = useState({ fournisseur: '', montantDu: '', dateFacture: '', echeance: '', description: '' });
    const [editingSupplierDebtId, setEditingSupplierDebtId] = useState(null);

    const [longTermDebts, setLongTermDebts] = useState([]);
    const [newLongTermDebt, setNewLongTermDebt] = useState({ creancier: '', montantEmprunte: '', soldeEmprunt: '', dateEmprunt: '' });
    const [editingLongTermDebtId, setEditingLongTermDebtId] = useState(null);


    // =====================================================================
    // --- USE EFFECT - DÉPENSES GÉNÉRALES ---
    // =====================================================================
    useEffect(() => {
        if (!db || !schoolId || activeTab !== 'generalExpenses') return;
        setMessage('');
        setError('');
        const expensesCollectionRef = collection(db, 'schools', schoolId, 'depenses');
        const q = query(expensesCollectionRef, orderBy('date', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const expensesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setGeneralExpenses(expensesList);
        }, (error) => {
            console.error("Error fetching general expenses:", error);
            setError("Erreur lors du chargement des dépenses générales.");
        });
        return () => unsubscribe();
    }, [db, schoolId, activeTab, setMessage, setError]);

    const handleGeneralExpenseChange = (e) => {
        const { name, value } = e.target;
        setNewGeneralExpense(prev => ({ ...prev, [name]: value }));
    };

    const handleGeneralExpenseSubmit = async (e) => {
        e.preventDefault();
        // ... (validation inchangée)
        if (!newGeneralExpense.description || !newGeneralExpense.amount || !newGeneralExpense.date || !newGeneralExpense.category) {
            setError("Veuillez remplir tous les champs de la dépense générale.");
            return;
        }

        setMessage('');
        setError('');

        try {
            const expenseToSave = {
                ...newGeneralExpense,
                amount: parseFloat(newGeneralExpense.amount),
                date: new Date(newGeneralExpense.date),
                createdAt: new Date(),
                updatedAt: new Date(),
                schoolId: schoolId
            };

            if (editingGeneralExpenseId) {
                const expenseDocRef = doc(db, 'schools', schoolId, 'depenses', editingGeneralExpenseId);
                await updateDoc(expenseDocRef, expenseToSave);
                setMessage("Dépense générale mise à jour avec succès !");
            } else {
                await addDoc(collection(db, 'schools', schoolId, 'depenses'), expenseToSave);
                setMessage("Dépense générale ajoutée avec succès !");
            }
            setNewGeneralExpense({ description: '', amount: '', date: '', category: '' });
            setEditingGeneralExpenseId(null);
        } catch (e) {
            console.error("Erreur lors de l'ajout/mise à jour de la dépense générale: ", e);
            setError("Erreur lors de l'enregistrement de la dépense générale.");
        }
    };

    const handleEditGeneralExpense = (expense) => {
        setNewGeneralExpense({
            description: expense.description,
            amount: expense.amount.toString(),
            date: expense.date.toDate().toISOString().split('T')[0],
            category: expense.category
        });
        setEditingGeneralExpenseId(expense.id);
    };

    const handleDeleteGeneralExpense = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette dépense générale ?")) {
            setMessage('');
            setError('');
            try {
                await deleteDoc(doc(db, 'schools', schoolId, 'depenses', id));
                setMessage("Dépense générale supprimée avec succès !");
            } catch (e) {
                console.error("Erreur lors de la suppression de la dépense générale: ", e);
                setError("Erreur lors de la suppression de la dépense générale.");
            }
        }
    };

    // Catégories mises à jour pour être plus claires pour le Compte de Résultat
    const generalExpenseCategories = [
        'Charges de Personnel', // NOTE: Déjà ajoutée par la Paie, mais peut être utilisée manuellement
        'Fournitures de bureau & Scolaire', 'Loyer et Charges Locatives', 
        'Factures (eau, électricité, internet)', 'Maintenance et Réparations', 
        'Transport & Déplacements', 'Marketing & Publicité', 
        'Impôts et Taxes', 'Frais Financiers (intérêts)', 'Autres Charges d\'Exploitation'
    ];

    // =====================================================================
    // --- USE EFFECT - GESTION DE PAIE (Charges de Personnel) ---
    // =====================================================================
    useEffect(() => {
        if (!db || !schoolId || activeTab !== 'payrollManagement') return;
        setMessage('');
        setError('');

        // Fetch payroll entries (inchangé)
        const payrollCollectionRef = collection(db, 'schools', schoolId, 'payroll');
        const qPayroll = query(payrollCollectionRef, orderBy('date', 'desc'));
        const unsubscribePayroll = onSnapshot(qPayroll, (snapshot) => {
            const payrollList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPayrollData(payrollList);
        }, (error) => {
            console.error("Error fetching payroll data:", error);
            setError("Erreur lors du chargement des données de paie.");
        });

        // Fetch employees for dropdown (inchangé)
        const teachersCollectionRef = collection(db, 'schools', schoolId, 'enseignants');
        const qTeachers = query(teachersCollectionRef, orderBy('nom')); 

        const unsubscribeTeachers = onSnapshot(qTeachers, (snapshot) => {
            const teachersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'teacher' }));
            setEmployeesForPayroll(teachersList);
        }, (error) => {
            console.error("Error fetching employees for payroll:", error);
            setError("Erreur lors du chargement de la liste du personnel.");
        });

        return () => {
            unsubscribePayroll();
            unsubscribeTeachers();
        };
    }, [db, schoolId, activeTab, setMessage, setError]);

    const handlePayrollChange = (e) => {
        const { name, value } = e.target;
        if (name === 'employeeId') {
            const selectedEmployee = employeesForPayroll.find(emp => emp.id === value);
            setNewPayrollEntry(prev => ({
                ...prev,
                employeeId: value,
                employeeName: selectedEmployee ? `${selectedEmployee.prenom} ${selectedEmployee.nom}` : ''
            }));
        } else {
            setNewPayrollEntry(prev => ({ ...prev, [name]: value }));
        }
    };

    const handlePayrollSubmit = async (e) => {
        e.preventDefault();
        if (!newPayrollEntry.employeeId || !newPayrollEntry.employeeName || !newPayrollEntry.amount || !newPayrollEntry.date || !newPayrollEntry.period) {
            setError("Veuillez sélectionner un employé et remplir tous les champs de l'entrée de paie.");
            return;
        }

        setMessage('');
        setError('');

        try {
            const payrollToSave = {
                ...newPayrollEntry,
                amount: parseFloat(newPayrollEntry.amount),
                date: new Date(newPayrollEntry.date),
                schoolId: schoolId,
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            let payrollDocRef;

            if (editingPayrollId) {
                // 1. Mise à jour de l'entrée de paie
                payrollDocRef = doc(db, 'schools', schoolId, 'payroll', editingPayrollId);
                await updateDoc(payrollDocRef, payrollToSave);

                // NOTE IMPORTANTE: La mise à jour de l'entrée correspondante dans 'depenses' est COMPLEXE
                // car l'ID de la dépense n'est pas stocké ici (sauf si on le recherchait).
                // Pour simplifier, nous mettons uniquement à jour l'entrée de paie. 
                // Pour une cohérence parfaite, il faudrait supprimer/re-créer la dépense ou utiliser une Cloud Function.
                setMessage("Entrée de paie mise à jour (Note: la dépense générale associée n'est pas mise à jour automatiquement).");
                
            } else {
                // 1. Création de l'entrée de paie
                payrollDocRef = await addDoc(collection(db, 'schools', schoolId, 'payroll'), payrollToSave);

                // 2. AJOUT CRUCIAL: Créer une dépense correspondante (pour le Compte de Résultat)
                await addDoc(collection(db, 'schools', schoolId, 'depenses'), {
                    description: `Salaire net versé à ${newPayrollEntry.employeeName} - Période: ${newPayrollEntry.period}`,
                    amount: payrollToSave.amount,
                    date: payrollToSave.date,
                    category: 'Charges de Personnel', // Catégorie standard SYSCOHADA (Classe 6)
                    sourceRef: payrollDocRef.id, // Lien vers l'entrée de paie
                    isPayroll: true,
                    createdAt: new Date(),
                    schoolId: schoolId,
                });
                
                setMessage("Entrée de paie et dépense associée ajoutées avec succès !");
            }
            
            setNewPayrollEntry({ employeeId: '', employeeName: '', amount: '', date: '', period: '' });
            setEditingPayrollId(null);
        } catch (e) {
            console.error("Erreur lors de l'ajout/mise à jour de l'entrée de paie: ", e);
            setError("Erreur lors de l'enregistrement de l'entrée de paie.");
        }
    };

    // Les fonctions de modification et de suppression de paie restent inchangées, mais
    // la suppression complète nécessiterait aussi la suppression dans la collection `depenses`.
    // Pour l'instant, on maintient la suppression uniquement dans `payroll`.
    const handleEditPayroll = (entry) => {
        setNewPayrollEntry({
            employeeId: entry.employeeId,
            employeeName: entry.employeeName,
            amount: entry.amount.toString(),
            date: entry.date.toDate().toISOString().split('T')[0],
            period: entry.period
        });
        setEditingPayrollId(entry.id);
    };

    const handleDeletePayroll = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette entrée de paie ? (Note: La dépense générale associée devra être supprimée manuellement)")) {
            setMessage('');
            setError('');
            try {
                // Suppression dans la collection principale 'payroll'
                await deleteDoc(doc(db, 'schools', schoolId, 'payroll', id));
                setMessage("Entrée de paie supprimée avec succès !");
            } catch (e) {
                console.error("Erreur lors de la suppression de l'entrée de paie: ", e);
                setError("Erreur lors de la suppression de l'entrée de paie.");
            }
        }
    };
    
    // =====================================================================
    // --- NOUVEAU USE EFFECT - DETTES & EMPRUNTS (Passif Bilan) ---
    // =====================================================================

    useEffect(() => {
        if (!db || !schoolId || activeTab !== 'liabilitiesManagement') return;
        
        // 1. Fetch Dettes Fournisseurs (Passif Circulant - Classe 4)
        const debtsRef = collection(db, 'schools', schoolId, 'dettes_fournisseurs');
        const qDebts = query(debtsRef, orderBy('dateFacture', 'desc'));
        const unsubscribeDebts = onSnapshot(qDebts, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSupplierDebts(list);
        });

        // 2. Fetch Emprunts Long Terme (Passif Non Courant - Classe 1)
        const loansRef = collection(db, 'schools', schoolId, 'emprunts_lt');
        const qLoans = query(loansRef, orderBy('dateEmprunt', 'desc'));
        const unsubscribeLoans = onSnapshot(qLoans, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setLongTermDebts(list);
        });

        return () => {
            unsubscribeDebts();
            unsubscribeLoans();
        };
    }, [db, schoolId, activeTab]);

    // --- Fonctions CRUD Dettes Fournisseurs ---

    const handleSupplierDebtChange = (e) => {
        const { name, value } = e.target;
        setNewSupplierDebt(prev => ({ ...prev, [name]: value }));
    };

    const handleSupplierDebtSubmit = async (e) => {
        e.preventDefault();
        // Validation simple
        if (!newSupplierDebt.fournisseur || !newSupplierDebt.montantDu || !newSupplierDebt.dateFacture) {
            setError("Veuillez remplir les champs obligatoires pour la dette fournisseur.");
            return;
        }
        setMessage('');
        setError('');

        try {
            const debtToSave = {
                ...newSupplierDebt,
                montantDu: parseFloat(newSupplierDebt.montantDu),
                dateFacture: new Date(newSupplierDebt.dateFacture),
                echeance: newSupplierDebt.echeance ? new Date(newSupplierDebt.echeance) : null,
                createdAt: new Date(),
                schoolId: schoolId
            };
            
            if (editingSupplierDebtId) {
                const docRef = doc(db, 'schools', schoolId, 'dettes_fournisseurs', editingSupplierDebtId);
                await updateDoc(docRef, debtToSave);
                setMessage("Dette fournisseur mise à jour avec succès !");
            } else {
                await addDoc(collection(db, 'schools', schoolId, 'dettes_fournisseurs'), debtToSave);
                setMessage("Dette fournisseur ajoutée avec succès !");
            }
            setNewSupplierDebt({ fournisseur: '', montantDu: '', dateFacture: '', echeance: '', description: '' });
            setEditingSupplierDebtId(null);
        } catch (e) {
            console.error("Erreur lors de l'enregistrement de la dette fournisseur: ", e);
            setError("Erreur lors de l'enregistrement de la dette fournisseur.");
        }
    };

    // --- Fonctions CRUD Emprunts Long Terme ---

    const handleLongTermDebtChange = (e) => {
        const { name, value } = e.target;
        setNewLongTermDebt(prev => ({ ...prev, [name]: value }));
    };

    const handleLongTermDebtSubmit = async (e) => {
        e.preventDefault();
        // Validation simple
        if (!newLongTermDebt.creancier || !newLongTermDebt.montantEmprunte || !newLongTermDebt.dateEmprunt) {
            setError("Veuillez remplir les champs obligatoires pour l'emprunt.");
            return;
        }
        setMessage('');
        setError('');

        try {
            const loanToSave = {
                ...newLongTermDebt,
                montantEmprunte: parseFloat(newLongTermDebt.montantEmprunte),
                // Simplification: le solde initial est le montant emprunté
                soldeEmprunt: parseFloat(newLongTermDebt.montantEmprunte), 
                dateEmprunt: new Date(newLongTermDebt.dateEmprunt),
                createdAt: new Date(),
                schoolId: schoolId
            };
            
            if (editingLongTermDebtId) {
                // NOTE: La mise à jour de l'emprunt est complexe et devrait inclure les remboursements. 
                // Ici, on met à jour les infos de base.
                const docRef = doc(db, 'schools', schoolId, 'emprunts_lt', editingLongTermDebtId);
                await updateDoc(docRef, loanToSave);
                setMessage("Emprunt Long Terme mis à jour avec succès !");
            } else {
                await addDoc(collection(db, 'schools', schoolId, 'emprunts_lt'), loanToSave);
                setMessage("Emprunt Long Terme ajouté avec succès !");
            }
            setNewLongTermDebt({ creancier: '', montantEmprunte: '', soldeEmprunt: '', dateEmprunt: '' });
            setEditingLongTermDebtId(null);
        } catch (e) {
            console.error("Erreur lors de l'enregistrement de l'emprunt: ", e);
            setError("Erreur lors de l'enregistrement de l'emprunt.");
        }
    };

    // Fonctions de formatage (simplifiées)
    const formatAmount = (amount) => amount?.toLocaleString('fr-FR', { minimumFractionDigits: 0 }) || '0';
    const formatDate = (dateTimestamp) => dateTimestamp?.toDate ? dateTimestamp.toDate().toLocaleDateString('fr-FR') : 'N/A';
    

    // --- Fonction pour rendre le contenu de l'onglet sélectionné ---
    const renderTabContent = () => {
        if (!canManageExpenses) {
            return (
                <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
                    <p>Votre rôle ({userRole}) ne vous permet pas de gérer ces transactions de dépenses/passifs.</p>
                </div>
            );
        }
            
         switch (activeTab) {
            case 'generalExpenses':
                return (
                    <div>
                        {/* Formulaire des Dépenses Générales */}
                        <form onSubmit={handleGeneralExpenseSubmit} className="mb-8 p-6 bg-gray-50 rounded-lg shadow-inner border border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-700 mb-4">{editingGeneralExpenseId ? 'Modifier une dépense générale' : 'Ajouter une nouvelle dépense générale'}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="generalDescription" className="block text-sm font-medium text-gray-700">Description</label>
                                    <input
                                        type="text"
                                        id="generalDescription"
                                        name="description"
                                        value={newGeneralExpense.description}
                                        onChange={handleGeneralExpenseChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="Ex: Achat de fournitures"
                                        required
                                        disabled={!canManageExpenses} // <--- AJOUTÉ
                                    />
                                </div>
                                <div>
                                    <label htmlFor="generalAmount" className="block text-sm font-medium text-gray-700">Montant (F CFA)</label>
                                    <input
                                        type="number"
                                        id="generalAmount"
                                        name="amount"
                                        value={newGeneralExpense.amount}
                                        onChange={handleGeneralExpenseChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="Ex: 50000"
                                        required
                                        min="0"
                                        disabled={!canManageExpenses} // <--- AJOUTÉ
                                    />
                                </div>
                                <div>
                                    <label htmlFor="generalDate" className="block text-sm font-medium text-gray-700">Date</label>
                                    <input
                                        type="date"
                                        id="generalDate"
                                        name="date"
                                        value={newGeneralExpense.date}
                                        onChange={handleGeneralExpenseChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        required
                                        disabled={!canManageExpenses} // <--- AJOUTÉ
                                    />
                                </div>
                                <div>
                                    <label htmlFor="generalCategory" className="block text-sm font-medium text-gray-700">Catégorie</label>
                                    <select
                                        id="generalCategory"
                                        name="category"
                                        value={newGeneralExpense.category}
                                        onChange={handleGeneralExpenseChange}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        required
                                        disabled={!canManageExpenses} // <--- AJOUTÉ
                                    >
                                        <option value="">Sélectionnez une catégorie</option>
                                        {generalExpenseCategories.map((cat, index) => (
                                            <option key={index} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="mt-6">
                                <button
                                    type="submit"
                                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    disabled={!canManageExpenses} // <--- AJOUTÉ
                                >
                                    {editingGeneralExpenseId ? 'Mettre à jour la dépense' : 'Ajouter la dépense'}
                                </button>
                                {editingGeneralExpenseId && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditingGeneralExpenseId(null);
                                            setNewGeneralExpense({ description: '', amount: '', date: '', category: '' });
                                        }}
                                        className="ml-4 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        disabled={!canManageExpenses} // <--- AJOUTÉ
                                    >
                                        Annuler
                                    </button>
                                )}
                            </div>
                        </form>

                        <h3 className="text-xl font-semibold text-gray-700 mb-4">Liste des Dépenses Générales (Charges d'Exploitation)</h3>
                        {generalExpenses.length === 0 ? (
                            <p className="text-gray-600 italic">Aucune dépense générale enregistrée pour le moment.</p>
                        ) : (
                            <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant (F CFA)</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Catégorie</th>
                                            {canManageExpenses && ( // <--- AJOUTÉ : Condition pour afficher la colonne Actions
                                                <th scope="col" className="relative px-6 py-3">
                                                    <span className="sr-only">Actions</span>
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {generalExpenses.map((expense) => (
                                            <tr key={expense.id} className={expense.isPayroll ? 'bg-indigo-50 hover:bg-indigo-100' : 'hover:bg-gray-50'}>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                                    {formatDate(expense.date)}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{expense.description}</td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-red-600 font-medium">-{formatAmount(expense.amount)}</td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                                                    {expense.category}
                                                    {expense.isPayroll && <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-200 text-indigo-800">Paie</span>}
                                                </td>
                                                {canManageExpenses && ( // <--- AJOUTÉ : Condition pour afficher les boutons d'action
                                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                                        <button
                                                            onClick={() => handleEditGeneralExpense(expense)}
                                                            className="text-blue-600 hover:text-blue-900 mr-4"
                                                            disabled={expense.isPayroll || !canManageExpenses} // <--- MODIFIÉ
                                                        >
                                                            Modifier
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteGeneralExpense(expense.id)}
                                                            className="text-red-600 hover:text-red-900"
                                                            disabled={expense.isPayroll || !canManageExpenses} // <--- MODIFIÉ
                                                        >
                                                            Supprimer
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <p className="mt-4 text-sm text-gray-500 italic">
                            **Note:** Les entrées marquées "Paie" sont gérées par l'onglet Gestion de Paie et ne devraient pas être modifiées/supprimées directement ici.
                        </p>
                    </div>
                );
            case 'payrollManagement':
                return (
                    <div className="p-4 bg-gray-50 rounded-md shadow-inner border border-gray-200">
                        {/* Formulaire de paie */}
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center"><Briefcase className="mr-2" size={20} /> Gestion de Paie (Charges de Personnel)</h3>
                        <form onSubmit={handlePayrollSubmit} className="mb-8 p-6 bg-white rounded-lg shadow">
                             <h4 className="text-lg font-semibold text-gray-700 mb-4">{editingPayrollId ? 'Modifier une entrée de paie' : 'Ajouter une nouvelle entrée de paie'}</h4>
                             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                     <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700">Sélectionner un employé</label>
                                     <select
                                         id="employeeId"
                                         name="employeeId"
                                         value={newPayrollEntry.employeeId}
                                         onChange={handlePayrollChange}
                                         className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                         required
                                         disabled={!canManageExpenses} // <--- AJOUTÉ
                                     >
                                         <option value="">-- Sélectionnez --</option>
                                         {employeesForPayroll.map(employee => (
                                             <option key={employee.id} value={employee.id}>
                                                 {employee.nom} {employee.prenom} ({employee.type === 'teacher' ? 'Professeur' : 'Personnel'})
                                             </option>
                                         ))}
                                     </select>
                                 </div>
                                 <input type="hidden" name="employeeName" value={newPayrollEntry.employeeName} />
                                 <div>
                                     <label htmlFor="payrollAmount" className="block text-sm font-medium text-gray-700">Montant (F CFA)</label>
                                     <input
                                         type="number"
                                         id="payrollAmount"
                                         name="amount"
                                         value={newPayrollEntry.amount}
                                         onChange={handlePayrollChange}
                                         className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                         required min="0"
                                         disabled={!canManageExpenses} // <--- AJOUTÉ
                                     />
                                 </div>
                                 <div>
                                     <label htmlFor="payrollDate" className="block text-sm font-medium text-gray-700">Date de paiement</label>
                                     <input
                                         type="date"
                                         id="payrollDate"
                                         name="date"
                                         value={newPayrollEntry.date}
                                         onChange={handlePayrollChange}
                                         className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                         required
                                         disabled={!canManageExpenses} // <--- AJOUTÉ
                                     />
                                 </div>
                                 <div>
                                     <label htmlFor="payrollPeriod" className="block text-sm font-medium text-gray-700">Période de paie</label>
                                     <input
                                         type="text"
                                         id="payrollPeriod"
                                         name="period"
                                         value={newPayrollEntry.period}
                                         onChange={handlePayrollChange}
                                         className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                         placeholder="Ex: Mars 2024" required
                                         disabled={!canManageExpenses} // <--- AJOUTÉ
                                     />
                                 </div>
                             </div>
                             <div className="mt-6">
                                 <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                    disabled={!canManageExpenses} // <--- AJOUTÉ
                                 >
                                     {editingPayrollId ? 'Mettre à jour la paie' : 'Ajouter la paie'}
                                 </button>
                                 {editingPayrollId && (
                                     <button type="button" onClick={() => { setEditingPayrollId(null); setNewPayrollEntry({ employeeId: '', employeeName: '', amount: '', date: '', period: '' }); }}
                                         className="ml-4 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                         disabled={!canManageExpenses} // <--- AJOUTÉ
                                     >
                                         Annuler
                                     </button>
                                 )}
                             </div>
                         </form>
                         <h4 className="text-xl font-semibold text-gray-700 mb-4">Historique des Paies</h4>
                         {payrollData.length === 0 ? (
                            <p className="text-gray-600 italic">Aucune entrée de paie enregistrée pour le moment.</p>
                         ) : (
                            <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employé</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant (F CFA)</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                                            {canManageExpenses && ( // <--- AJOUTÉ
                                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {payrollData.map((entry) => (
                                            <tr key={entry.id}>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{formatDate(entry.date)}</td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{entry.employeeName}</td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-red-600 font-medium">-{formatAmount(entry.amount)}</td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">{entry.period}</td>
                                                {canManageExpenses && ( // <--- AJOUTÉ
                                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                                        <button onClick={() => handleEditPayroll(entry)} className="text-blue-600 hover:text-blue-900 mr-4"
                                                            disabled={!canManageExpenses} // <--- AJOUTÉ
                                                        >Modifier</button>
                                                        <button onClick={() => handleDeletePayroll(entry.id)} className="text-red-600 hover:text-red-900"
                                                            disabled={!canManageExpenses} // <--- AJOUTÉ
                                                        >Supprimer</button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                         )}
                         <p className="mt-4 p-3 text-sm bg-blue-50 border border-blue-200 rounded text-gray-700 italic">
                             **Intégration financière :** Chaque nouvelle entrée de paie est automatiquement ajoutée à la collection `depenses` avec la catégorie 'Charges de Personnel' pour être prise en compte dans le Compte de Résultat.
                         </p>
                    </div>
                );
            case 'liabilitiesManagement':
                return (
                    <div className="space-y-8">
                        {/* --- Section Dettes Fournisseurs (Passif Circulant) --- */}
                        <div className="p-6 bg-yellow-50 rounded-lg shadow-inner border border-yellow-200">
                            <h3 className="text-xl font-semibold text-yellow-800 mb-4 flex items-center"><Zap className="mr-2" size={20} /> Dettes Fournisseurs (Court Terme)</h3>
                            
                            {/* Formulaire des Dettes Fournisseurs */}
                            <form onSubmit={handleSupplierDebtSubmit} className="mb-6 p-4 bg-white rounded-lg shadow-sm">
                                <h4 className="text-lg font-semibold mb-3">Ajouter une Dette</h4>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <input type="text" name="fournisseur" value={newSupplierDebt.fournisseur} onChange={handleSupplierDebtChange} placeholder="Fournisseur (Nom)" className="px-3 py-2 border rounded" required disabled={!canManageExpenses} />
                                    <input type="number" name="montantDu" value={newSupplierDebt.montantDu} onChange={handleSupplierDebtChange} placeholder="Montant dû (F CFA)" className="px-3 py-2 border rounded" required min="0" disabled={!canManageExpenses} />
                                    <input type="date" name="dateFacture" value={newSupplierDebt.dateFacture} onChange={handleSupplierDebtChange} placeholder="Date Facture" className="px-3 py-2 border rounded" required disabled={!canManageExpenses} />
                                    <input type="date" name="echeance" value={newSupplierDebt.echeance} onChange={handleSupplierDebtChange} placeholder="Date d'Échéance" className="px-3 py-2 border rounded" disabled={!canManageExpenses} />
                                    <div className="md:col-span-4">
                                        <input type="text" name="description" value={newSupplierDebt.description} onChange={handleSupplierDebtChange} placeholder="Description (Ex: Achat de manuels)" className="w-full px-3 py-2 border rounded" disabled={!canManageExpenses} />
                                    </div>
                                </div>
                                <button type="submit" className="mt-4 py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
                                    disabled={!canManageExpenses} // <--- AJOUTÉ
                                >
                                    Ajouter la Dette
                                </button>
                            </form>
                            
                            <h4 className="text-lg font-semibold mt-6">Liste des Dettes ({formatAmount(supplierDebts.reduce((sum, d) => sum + (d.montantDu || 0), 0))} F CFA)</h4>
                            {/* Tableau de visualisation des dettes fournisseurs */}
                            {supplierDebts.length > 0 ? (
                                <div className="overflow-x-auto mt-2">
                                    <table className="min-w-full divide-y divide-gray-300 bg-white">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Montant Dû</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date Facture</th>
                                                {canManageExpenses && ( // <--- AJOUTÉ
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {supplierDebts.map(debt => (
                                                <tr key={debt.id}>
                                                    <td className="px-4 py-2 text-sm">{debt.fournisseur}</td>
                                                    <td className="px-4 py-2 text-sm font-medium">{formatAmount(debt.montantDu)}</td>
                                                    <td className="px-4 py-2 text-sm">{formatDate(debt.dateFacture)}</td>
                                                    {canManageExpenses && ( // <--- AJOUTÉ
                                                        <td className="px-4 py-2 text-sm font-medium">
                                                            <button onClick={() => { /* Implémenter edit */ }} className="text-blue-600 hover:text-blue-900 mr-2"
                                                                disabled={!canManageExpenses} // <--- AJOUTÉ
                                                            >Modifier</button>
                                                            <button onClick={async () => { await deleteDoc(doc(db, 'schools', schoolId, 'dettes_fournisseurs', debt.id)); setMessage("Dette supprimée !"); }} className="text-red-600 hover:text-red-900"
                                                                disabled={!canManageExpenses} // <--- AJOUTÉ
                                                            >Supprimer</button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (<p className="text-gray-600 italic mt-2">Aucune dette fournisseur enregistrée.</p>)}

                        </div>

                        {/* --- Section Emprunts Long Terme (Passif Non Courant) --- */}
                        <div className="p-6 bg-indigo-50 rounded-lg shadow-inner border border-indigo-200">
                            <h3 className="text-xl font-semibold text-indigo-800 mb-4 flex items-center"><DollarSign className="mr-2" size={20} /> Emprunts Long Terme (Passif Non Courant)</h3>
                            
                            {/* Formulaire des Emprunts Long Terme */}
                            <form onSubmit={handleLongTermDebtSubmit} className="mb-6 p-4 bg-white rounded-lg shadow-sm">
                                <h4 className="text-lg font-semibold mb-3">Ajouter un Emprunt</h4>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <input type="text" name="creancier" value={newLongTermDebt.creancier} onChange={handleLongTermDebtChange} placeholder="Créancier (Banque/Institution)" className="px-3 py-2 border rounded" required disabled={!canManageExpenses} />
                                    <input type="number" name="montantEmprunte" value={newLongTermDebt.montantEmprunte} onChange={handleLongTermDebtChange} placeholder="Montant Emprunté (F CFA)" className="px-3 py-2 border rounded" required min="0" disabled={!canManageExpenses} />
                                    <input type="date" name="dateEmprunt" value={newLongTermDebt.dateEmprunt} onChange={handleLongTermDebtChange} placeholder="Date de l'Emprunt" className="px-3 py-2 border rounded" required disabled={!canManageExpenses} />
                                </div>
                                <button type="submit" className="mt-4 py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                                    disabled={!canManageExpenses} // <--- AJOUTÉ
                                >
                                    Ajouter l'Emprunt
                                </button>
                            </form>

                            <h4 className="text-lg font-semibold mt-6">Liste des Emprunts</h4>
                            {longTermDebts.length > 0 ? (
                                <div className="overflow-x-auto mt-2">
                                    <table className="min-w-full divide-y divide-gray-300 bg-white">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Créancier</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Montant Initial</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Solde Actuel</th>
                                                {canManageExpenses && ( // <--- AJOUTÉ
                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {longTermDebts.map(loan => (
                                                <tr key={loan.id}>
                                                    <td className="px-4 py-2 text-sm">{loan.creancier}</td>
                                                    <td className="px-4 py-2 text-sm font-medium">{formatAmount(loan.montantEmprunte)}</td>
                                                    <td className="px-4 py-2 text-sm font-bold text-red-700">{formatAmount(loan.soldeEmprunt)}</td>
                                                    {canManageExpenses && ( // <--- AJOUTÉ
                                                        <td className="px-4 py-2 text-sm font-medium">
                                                            <button onClick={() => { /* Implémenter edit */ }} className="text-blue-600 hover:text-blue-900 mr-2"
                                                                disabled={!canManageExpenses} // <--- AJOUTÉ
                                                            >Modifier</button>
                                                            <button onClick={async () => { await deleteDoc(doc(db, 'schools', schoolId, 'emprunts_lt', loan.id)); setMessage("Emprunt supprimé !"); }} className="text-red-600 hover:text-red-900"
                                                                disabled={!canManageExpenses} // <--- AJOUTÉ
                                                            >Supprimer</button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (<p className="text-gray-600 italic mt-2">Aucun emprunt à long terme enregistré.</p>)}
                        </div>

                        <p className="mt-4 p-3 text-sm bg-blue-50 border border-blue-200 rounded text-gray-700 italic">
                            **Note SYSCOHADA :** Les données de cette section alimentent le Passif du Bilan. Le montant total des dettes est repris en Passif Circulant, et le solde des emprunts est repris en Passif Non Courant / Capitaux Propres et Ressources Assimilées.
                        </p>
                    </div>
                );
            case 'materialManagement':
                return (
                    <div className="p-4 bg-gray-50 rounded-md shadow-inner border border-gray-200">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center"><Briefcase className="mr-2" size={20} /> Gestion du Matériel et Mobilier (Actif Permanent)</h3>
                        {/* Le composant MaterielMobilierManagement doit gérer ses propres permissions en interne */}
                        {canManageExpenses ? ( // <--- AJOUTÉ : Condition pour charger le composant imbriqué
                            <MaterielMobilierManagement db={db} schoolId={schoolId} userRole={userRole} setMessage={setMessage} setError={setError} />
                        ) : (
                            <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
                                <p>Votre rôle ({userRole}) ne vous permet pas de gérer le matériel et le mobilier.</p>
                            </div>
                        )}
                        <p className="mt-4 p-3 text-sm bg-green-50 border border-green-200 rounded text-gray-700 italic">
                             **Intégration financière :** Les acquisitions de matériel sont considérées comme des **Immobilisations** et alimentent la section Actif Permanent du Bilan.
                        </p>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Gestion des Transactions Financières (Dépenses & Passifs)</h2>

            {/* Barre d'onglets (Mise à jour) */}
            <div className="mb-6 border-b border-gray-200">
                <ul className="flex flex-wrap -mb-px text-sm font-medium text-center" role="tablist">
                    <li className="mr-2" role="presentation">
                        <button
                            className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'generalExpenses' ? 'text-blue-600 border-blue-600' : 'text-gray-500 hover:text-gray-600 hover:border-gray-300'}`}
                            onClick={() => setActiveTab('generalExpenses')}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'generalExpenses'}
                        >
                            Dépenses Générales (Charges)
                        </button>
                    </li>
                    <li className="mr-2" role="presentation">
                        <button
                            className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'payrollManagement' ? 'text-blue-600 border-blue-600' : 'text-gray-500 hover:text-gray-600 hover:border-gray-300'}`}
                            onClick={() => setActiveTab('payrollManagement')}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'payrollManagement'}
                        >
                            Gestion de Paie
                        </button>
                    </li>
                    <li className="mr-2" role="presentation">
                        <button
                            className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'liabilitiesManagement' ? 'text-blue-600 border-blue-600' : 'text-gray-500 hover:text-gray-600 hover:border-gray-300'}`}
                            onClick={() => setActiveTab('liabilitiesManagement')}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'liabilitiesManagement'}
                        >
                            Dettes & Emprunts (Passifs)
                        </button>
                    </li>
                    <li className="mr-2" role="presentation">
                        <button
                            className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'materialManagement' ? 'text-blue-600 border-blue-600' : 'text-gray-500 hover:text-gray-600 hover:border-gray-300'}`}
                            onClick={() => setActiveTab('materialManagement')}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'materialManagement'}
                        >
                            Gestion du Matériel (Actifs)
                        </button>
                    </li>
                </ul>
            </div>

            {/* Contenu des onglets */}
            <div id="expenses-management-content">
                {/* Condition globale pour le contenu principal */}
                {!canManageExpenses && (
                    <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md mb-6">
                        <p>Votre rôle ({userRole}) ne vous permet pas de gérer ces transactions de dépenses/passifs.</p>
                    </div>
                )}
                {renderTabContent()}
            </div>
            
            <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                <p className="font-medium">
                    **Rappel sur la Cohérence :** Les données gérées dans ces onglets alimentent directement les états financiers :
                    <ul className="list-disc ml-6 mt-2 text-sm">
                        <li>**Dépenses Générales & Paie** ➡️ **Compte de Résultat** (Charges d'Exploitation) et **Trésorerie**.</li>
                        <li>**Dettes & Emprunts** ➡️ **Bilan** (Passif : Passif Circulant et Passif Non Courant).</li>
                        <li>**Gestion du Matériel** ➡️ **Bilan** (Actif Permanent : Immobilisations).</li>
                    </ul>
                </p>
            </div>
        </div>
    );
};


export default DepensesManagement;